/**
 * Regras de compatibilidade do confronto. Cada regra devolve achados só quando NÃO está conforme.
 * Catálogo (nome, severidade, ativo, fundamento) em public.fiscal_regra.
 *
 * Princípios:
 *   * Dado ausente no XML não vira reprovação: a regra que depende dele não roda (ou devolve DEPENDE).
 *   * DIVERGENTE = incompatibilidade objetiva com a base; DEPENDE = configuração atípica que pede olho humano.
 */
import type { BaseConhecimento, CfopBase, FcpNcmRegra } from './base';
import { avaliarServico, SEVERIDADE_SERVICO } from './regras-servico';
import { TOLERANCIA_BASE_IPI, basesIcmsComESemIpi, ipiPeloIndFinal } from './ipiBaseIcms';
import type { Achado, IcmsLido, ItemLido, NotaLida, Resultado, Severidade } from './tipos';
import { ehNfse } from './tipos';

type NovoAchado = Omit<Achado, 'severidade'> & { severidade?: Severidade };

/** Severidade padrão de cada regra — espelho de public.fiscal_regra, usado se o catálogo do banco não tiver a regra. */
export const SEVERIDADE_PADRAO: Record<string, Severidade> = {
  'NF-DEST-01': 'erro',
  'NF-DEST-02': 'erro',
  'IT-CFOP-01': 'erro',
  'IT-CFOP-02': 'erro',
  'IT-CFOP-03': 'erro',
  'IT-CFOP-04': 'erro',
  'IT-CFOP-05': 'erro',
  'IT-CFOP-06': 'erro',
  'IT-CFOP-07': 'alerta',
  'IT-ICMS-01': 'erro',
  'IT-ICMS-02': 'erro',
  'IT-ICMS-03': 'erro',
  'IT-ICMS-04': 'alerta',
  'IT-ICMS-05': 'info',
  'IT-ICMS-06': 'alerta',
  'IT-IPI-01': 'erro',
  'IT-PISCOF-01': 'erro',
  'IT-PISCOF-02': 'alerta',
  'IT-NCM-01': 'erro',
  'IT-IBS-01': 'erro',
  'IT-DIFAL-01': 'erro',
  'IT-DIFAL-02': 'erro',
  'IT-DIFAL-03': 'erro',
  'IT-ST-01': 'alerta',
  'IT-ST-02': 'alerta',
  'IT-CBENEF-01': 'alerta',
  'IT-VALOR-01': 'erro',
  'NF-VALOR-01': 'erro',
  ...SEVERIDADE_SERVICO,
};

export class Avaliacao {
  readonly achados: Achado[] = [];
  constructor(private readonly base: BaseConhecimento) {}

  /** Registra o achado se a regra estiver ativa; severidade padrão vem do catálogo. */
  add(a: NovoAchado) {
    const regra = this.base.regras.get(a.regra);
    if (regra && !regra.ativo) return;
    this.achados.push({ ...a, severidade: a.severidade ?? regra?.severidade ?? SEVERIDADE_PADRAO[a.regra] ?? 'alerta' });
  }
}

/** Achado justificado por observação do cliente não piora o resultado. */
export const piorResultado = (achados: Achado[]): Resultado => {
  const valem = achados.filter((a) => !a.justificado);
  return valem.some((a) => a.resultado === 'DIVERGENTE') ? 'DIVERGENTE' : valem.some((a) => a.resultado === 'DEPENDE') ? 'DEPENDE' : 'CONFORME';
};

/** Modelo da nota → documento da tabela de cClassTrib (fiscal_cclasstrib_documento). */
const MODELOS_NFE: Record<string, string> = { '55': 'NFE', '65': 'NFCE', NFSE: 'NFSE' };
const NOME_DOCUMENTO: Record<string, string> = { NFE: 'NF-e', NFCE: 'NFC-e', NFSE: 'NFS-e' };

const DOMINIO_CAMPO_ICMS: [keyof Pick<IcmsLido, 'modBC' | 'modBCST' | 'motDesICMS' | 'motDesICMSST'>, string, string][] = [
  ['modBC', 'MOD_BC', 'modBC'],
  ['modBCST', 'MOD_BC_ST', 'modBCST'],
  ['motDesICMS', 'MOT_DES_ICMS', 'motDesICMS'],
  ['motDesICMSST', 'MOT_DES_ICMS_ST', 'motDesICMSST'],
];

/** Sentido (entrada/saída) como aparece nas mensagens. */
const SENTIDO_TEXTO: Record<string, string> = { entrada: 'entrada', saida: 'saída', ambos: 'entrada e saída' };

const foraDaVigencia = (data: string | null, inicio: string | null, fim: string | null) =>
  !!data && ((inicio !== null && data < inicio) || (fim !== null && data > fim));

/**
 * Consistência aritmética interna (base × alíquota = valor), não recálculo de tributo: usa os campos
 * como o próprio XML os declarou, então uma base já reduzida (pRedBC) ou uma alíquota fixa continuam
 * batendo. Tolerância de 2 centavos por arredondamento. Campo ausente não é comparado (é abstenção,
 * não conformidade nem divergência) — ex.: grupos não tributados (IPINT, PISNT) não têm esses campos.
 */
const TOLERANCIA_VALOR = 0.02;

function conferirValor(rotulo: string, vBC: number | null, pAliquota: number | null, vDeclarado: number | null, av: Avaliacao) {
  if (vBC === null || pAliquota === null || vDeclarado === null) return;
  const calculado = Math.round(vBC * pAliquota) / 100;
  if (Math.abs(calculado - vDeclarado) > TOLERANCIA_VALOR) {
    av.add({
      regra: 'IT-VALOR-01',
      resultado: 'DIVERGENTE',
      mensagem: `${rotulo}: base × alíquota (${vBC.toFixed(2)} × ${pAliquota}%) dá ${calculado.toFixed(2)}, mas o valor informado é ${vDeclarado.toFixed(2)}.`,
      encontrado: vDeclarado.toFixed(2),
      esperado: calculado.toFixed(2),
    });
  }
}

/**
 * ICMS próprio do item. Sem diferimento, base × alíquota = vICMS. Com diferimento declarado (vICMSOp, pDif ou vICMSDif —
 * CST 51 e afins), o vICMS é o que sobra depois de diferir, então as contas são três:
 *   vBC × pICMS = vICMSOp;   vICMSOp × pDif = vICMSDif;   vICMSOp − vICMSDif = vICMS.
 * Cada uma só é conferida quando o XML traz todos os campos dela; a operação cheia, se o XML não a declara, é a calculada.
 */
export function conferirIcms(icms: IcmsLido | null, av: Avaliacao) {
  if (!icms) return;
  const { vBC, pICMS, vICMS, vICMSOp, pDif, vICMSDif } = icms;
  if (vICMSOp === null && pDif === null && vICMSDif === null) {
    conferirValor('ICMS', vBC, pICMS, vICMS, av);
    return;
  }
  const divergente = (mensagem: string, encontrado: number, esperado: number) =>
    av.add({ regra: 'IT-VALOR-01', resultado: 'DIVERGENTE', mensagem, encontrado: encontrado.toFixed(2), esperado: esperado.toFixed(2) });
  const calculado = vBC !== null && pICMS !== null ? Math.round(vBC * pICMS) / 100 : null;
  if (calculado !== null && vICMSOp !== null && Math.abs(calculado - vICMSOp) > TOLERANCIA_VALOR) {
    divergente(`ICMS da operação (diferimento): base × alíquota (${vBC!.toFixed(2)} × ${pICMS}%) dá ${calculado.toFixed(2)}, mas o vICMSOp informado é ${vICMSOp.toFixed(2)}.`, vICMSOp, calculado);
  }
  const operacao = vICMSOp ?? calculado;
  if (operacao === null) return;
  if (pDif !== null && vICMSDif !== null) {
    const diferido = Math.round(operacao * pDif) / 100;
    if (Math.abs(diferido - vICMSDif) > TOLERANCIA_VALOR) {
      divergente(`ICMS diferido: ICMS da operação × percentual diferido (${operacao.toFixed(2)} × ${pDif}%) dá ${diferido.toFixed(2)}, mas o vICMSDif informado é ${vICMSDif.toFixed(2)}.`, vICMSDif, diferido);
    }
  }
  const diferido = vICMSDif ?? (pDif !== null ? Math.round(operacao * pDif) / 100 : null);
  if (diferido === null || vICMS === null) return;
  const devido = Math.round((operacao - diferido) * 100) / 100;
  if (Math.abs(devido - vICMS) > TOLERANCIA_VALOR) {
    divergente(`ICMS com diferimento: ICMS da operação − ICMS diferido (${operacao.toFixed(2)} − ${diferido.toFixed(2)}) dá ${devido.toFixed(2)}, mas o vICMS informado é ${vICMS.toFixed(2)}.`, vICMS, devido);
  }
}

/**
 * Soma um campo pelos itens e compara ao total declarado (total/ICMSTot ou total/ISSQNtot). Só compara quando o total
 * E todos os itens têm o campo — soma parcial (alguns itens sem o campo) não é comparável a nada; quem chama decide
 * quando "sem o campo" vale zero. A tolerância cresce com o número de itens porque cada um já arredondou o seu valor.
 */
function conferirTotal(rotulo: string, totalDeclarado: number | null, valoresItens: (number | null)[], av: Avaliacao, grupoTotal = 'ICMSTot') {
  if (totalDeclarado === null || valoresItens.length === 0 || valoresItens.some((v) => v === null)) return;
  const soma = Math.round(valoresItens.reduce<number>((s, v) => s + (v as number), 0) * 100) / 100;
  const tolerancia = TOLERANCIA_VALOR * valoresItens.length;
  if (Math.abs(soma - totalDeclarado) > tolerancia) {
    av.add({
      regra: 'NF-VALOR-01',
      resultado: 'DIVERGENTE',
      mensagem: `${rotulo}: ${valoresItens.length === 1 ? 'o único item' : `a soma dos ${valoresItens.length} itens`} dá ${soma.toFixed(2)}, mas o total da nota (${grupoTotal}) é ${totalDeclarado.toFixed(2)}.`,
      encontrado: totalDeclarado.toFixed(2),
      esperado: soma.toFixed(2),
    });
  }
}

// ---------------------------------------------------------------------------
// Regras de nota
// ---------------------------------------------------------------------------

export function avaliarNota(nota: NotaLida, av: Avaliacao) {
  // NFS-e: as regras de nota da NF-e (totais do ICMSTot, destino por UF) não se aplicam; as dela estão no item (o serviço).
  if (ehNfse(nota)) return;
  // Mercadoria (grupo ICMS) fecha contra o ICMSTot; serviço (grupo ISSQN, NF-e conjugada) fecha contra o ISSQNtot.
  const mercadorias = nota.itens.filter((i) => !i.issqn);
  const servicos = nota.itens.filter((i) => i.issqn);
  // ICMSTot/vProd só soma os itens com prod/indTot = 1.
  conferirTotal('vProd', nota.totais.vProd, mercadorias.filter((i) => i.indTot).map((i) => i.vProd), av);
  // Item com grupo ICMS sem vBC/vICMS (CST 40/41/60, CSOSN 102...) soma zero, como a SEFAZ soma o total — antes, um
  // único item assim deixava a nota inteira sem conferência de base e ICMS.
  const icmsOuZero = (i: ItemLido, campo: 'vBC' | 'vICMS') => (i.icms ? i.icms[campo] ?? 0 : null);
  conferirTotal('Base do ICMS (vBC)', nota.totais.vBC, mercadorias.map((i) => icmsOuZero(i, 'vBC')), av);
  conferirTotal('ICMS (vICMS)', nota.totais.vICMS, mercadorias.map((i) => icmsOuZero(i, 'vICMS')), av);
  conferirTotal('IPI (vIPI)', nota.totais.vIPI, mercadorias.map((i) => i.ipi?.vTributo ?? null), av);
  conferirTotal('PIS (vPIS)', nota.totais.vPIS, mercadorias.map((i) => i.pis?.vTributo ?? null), av);
  conferirTotal('COFINS (vCOFINS)', nota.totais.vCOFINS, mercadorias.map((i) => i.cofins?.vTributo ?? null), av);
  if (servicos.length) {
    const t = nota.totaisIssqn;
    if (!t) {
      av.add({
        regra: 'NF-VALOR-01',
        resultado: 'DIVERGENTE',
        mensagem: `Nota com ${servicos.length} ${servicos.length === 1 ? 'item' : 'itens'} de serviço (grupo ISSQN) sem o grupo de totais ISSQNtot.`,
        encontrado: 'ISSQNtot ausente',
      });
    } else {
      conferirTotal('Serviços (vServ)', t.vServ, servicos.filter((i) => i.indTot).map((i) => i.vProd), av, 'ISSQNtot');
      conferirTotal('ISS (vISS)', t.vISS, servicos.map((i) => i.issqn!.vISSQN), av, 'ISSQNtot');
      conferirTotal('PIS sobre serviços (vPIS)', t.vPIS, servicos.map((i) => i.pis?.vTributo ?? null), av, 'ISSQNtot');
      conferirTotal('COFINS sobre serviços (vCOFINS)', t.vCOFINS, servicos.map((i) => i.cofins?.vTributo ?? null), av, 'ISSQNtot');
    }
  }

  const { idDest, indFinal, indPres } = nota;
  const ie = nota.dest.indIEDest;

  if (ie === '9' && idDest === '2' && indFinal === '0') {
    av.add({
      regra: 'NF-DEST-01',
      resultado: 'DIVERGENTE',
      mensagem: 'Destinatário não contribuinte em operação interestadual sem indicar consumidor final.',
      encontrado: 'indIEDest 9 · idDest 2 · indFinal 0',
      esperado: 'indFinal 1',
    });
  }

  const ufEmit = nota.emit.uf;
  const ufDest = nota.dest.uf;
  if (idDest && ufEmit && ufDest) {
    const exterior = ufDest === 'EX';
    const mesmaUf = ufEmit === ufDest;
    let esperado: string | null = null;
    if (exterior && idDest !== '3') esperado = '3 (exterior)';
    else if (!exterior && idDest === '3') esperado = mesmaUf ? '1 (interna)' : '2 (interestadual)';
    else if (!exterior && mesmaUf && idDest === '2') esperado = '1 (interna)';
    else if (!exterior && !mesmaUf && idDest === '1') {
      // Venda presencial a consumidor final de outra UF é operação interna (idDest 1).
      const presencialConsumidor = indFinal === '1' && (indPres === '1' || indPres === '5');
      if (!presencialConsumidor) esperado = '2 (interestadual)';
    }
    if (esperado) {
      av.add({
        regra: 'NF-DEST-02',
        resultado: 'DIVERGENTE',
        mensagem: `idDest ${idDest} incompatível com emitente em ${ufEmit} e destinatário em ${ufDest}.`,
        encontrado: `idDest ${idDest} · ${ufEmit}→${ufDest}`,
        esperado: `idDest ${esperado}`,
      });
    }
  }
}

// ---------------------------------------------------------------------------
// Regras de item
// ---------------------------------------------------------------------------

function regrasCfop(nota: NotaLida, item: ItemLido, base: BaseConhecimento, av: Avaliacao): CfopBase | null {
  const codigo = item.cfop?.replace(/\D/g, '') ?? null;
  const cfop = codigo ? base.cfop.get(codigo) : undefined;

  if (!codigo || !cfop || cfop.nivel !== 'codigo') {
    av.add({
      regra: 'IT-CFOP-01',
      resultado: 'DIVERGENTE',
      mensagem: !codigo ? 'Item sem CFOP.' : !cfop ? `CFOP ${codigo} não existe na tabela oficial.` : `CFOP ${codigo} é título de ${cfop.nivel}, não código lançável.`,
      encontrado: codigo,
    });
    return null;
  }

  if (foraDaVigencia(nota.dataEmissao, cfop.vigenciaInicio, cfop.vigenciaFim)) {
    av.add({
      regra: 'IT-CFOP-02',
      resultado: 'DIVERGENTE',
      mensagem: `CFOP ${codigo} fora de vigência em ${nota.dataEmissao}.`,
      encontrado: nota.dataEmissao,
      esperado: `${cfop.vigenciaInicio ?? '—'} a ${cfop.vigenciaFim ?? 'sem término'}`,
    });
  }

  if (nota.modelo && MODELOS_NFE[nota.modelo] && cfop.indNfe === 0) {
    av.add({ regra: 'IT-CFOP-03', resultado: 'DIVERGENTE', mensagem: `CFOP ${codigo} não é aceito em NF-e/NFC-e (indNFe = 0).`, encontrado: codigo });
  }

  if (nota.tpNF && cfop.tpNF !== nota.tpNF) {
    av.add({
      regra: 'IT-CFOP-04',
      resultado: 'DIVERGENTE',
      mensagem: `CFOP de ${SENTIDO_TEXTO[cfop.sentido] ?? cfop.sentido} (${codigo}) em nota de ${nota.tpNF === '1' ? 'saída' : 'entrada'}.`,
      encontrado: `tpNF ${nota.tpNF} · CFOP ${codigo}`,
      esperado: `tpNF ${cfop.tpNF}`,
    });
  }

  if (nota.idDest && cfop.idDest !== nota.idDest) {
    av.add({
      regra: 'IT-CFOP-05',
      resultado: 'DIVERGENTE',
      mensagem: `CFOP ${codigo} é de operação ${ { '1': 'interna', '2': 'interestadual', '3': 'com exterior' }[cfop.idDest] } e a nota tem idDest ${nota.idDest}.`,
      encontrado: `idDest ${nota.idDest} · CFOP ${codigo}`,
      esperado: `idDest ${cfop.idDest} ou CFOP do grupo correspondente`,
    });
  }

  if (nota.finNFe) {
    const fin4 = nota.finNFe === '4';
    if (cfop.indDevol === 1 && !fin4) {
      av.add({
        regra: 'IT-CFOP-06',
        resultado: 'DIVERGENTE',
        mensagem: `CFOP de devolução (${codigo}) em nota com finNFe ${nota.finNFe}.`,
        encontrado: `finNFe ${nota.finNFe}`,
        esperado: 'finNFe 4',
      });
    } else if (fin4 && cfop.indDevol === 0 && cfop.indRetor !== 1) {
      av.add({
        regra: 'IT-CFOP-06',
        resultado: 'DIVERGENTE',
        mensagem: `Nota de devolução (finNFe 4) com CFOP ${codigo}, que não é de devolução nem de retorno.`,
        encontrado: `finNFe 4 · CFOP ${codigo}`,
        esperado: 'CFOP com indDevol = 1',
      });
    }
  }

  const ie = nota.dest.indIEDest;
  if (ie === '1' && cfop.atributos.includes('NAO_CONTRIBUINTE') && !/contribuinte ou a n[aã]o contribuinte/i.test(cfop.descricao)) {
    av.add({
      regra: 'IT-CFOP-07',
      resultado: 'DIVERGENTE',
      mensagem: `CFOP ${codigo} é de operação com não contribuinte/consumidor final, mas o destinatário é contribuinte (indIEDest 1).`,
      encontrado: `indIEDest 1 · CFOP ${codigo}`,
      esperado: 'CFOP de operação com contribuinte',
    });
  }
  return cfop;
}

function regrasIcms(nota: NotaLida, item: ItemLido, cfop: CfopBase | null, base: BaseConhecimento, av: Avaliacao) {
  const icms = item.icms;
  if (!icms) return;
  const rotulo = icms.tabela === 'ICMS_CSOSN' ? 'CSOSN' : 'CST';

  const crt = nota.emit.crt;
  const regimes = base.regime.get(icms.tabela);
  if (crt && regimes && !regimes.has(crt)) {
    av.add({
      regra: 'IT-ICMS-01',
      resultado: 'DIVERGENTE',
      mensagem: `${rotulo} ${icms.cst ?? '(vazio)'} informado com emitente CRT ${crt}: ${crt === '1' || crt === '4' ? 'Simples Nacional usa CSOSN' : 'regime normal usa CST do ICMS'}.`,
      encontrado: `CRT ${crt} · ${icms.grupo}/${icms.cst ?? '—'}`,
      esperado: crt === '1' || crt === '4' ? 'CSOSN' : 'CST do ICMS',
    });
  }

  const cst = icms.cst ? base.cst.get(`${icms.tabela}|${icms.cst}`) : undefined;
  if (!icms.cst || !cst) {
    av.add({ regra: 'IT-ICMS-02', resultado: 'DIVERGENTE', mensagem: icms.cst ? `${rotulo} ${icms.cst} inexistente.` : `${rotulo} não informado.`, encontrado: `${icms.grupo}/${icms.cst ?? '—'}` });
    return;
  }
  if (!base.cstGrupo.has(`${icms.tabela}|${icms.cst}|${icms.grupo}`)) {
    av.add({
      regra: 'IT-ICMS-02',
      resultado: 'DIVERGENTE',
      mensagem: `${rotulo} ${icms.cst} informado no grupo ${icms.grupo}, que não o aceita.`,
      encontrado: `${icms.grupo}/${icms.cst}`,
    });
  }

  const valoresGrupo = base.grupoValor.get(icms.grupo);
  for (const [campo, dominio, nomeXml] of DOMINIO_CAMPO_ICMS) {
    const v = icms[campo];
    if (v === null) continue;
    const aceitos = valoresGrupo?.get(dominio);
    if (!aceitos || !aceitos.has(v)) {
      av.add({
        regra: 'IT-ICMS-03',
        resultado: 'DIVERGENTE',
        mensagem: aceitos
          ? `${nomeXml} ${v} não é aceito no grupo ${icms.grupo}.`
          : `O grupo ${icms.grupo} não tem o campo ${nomeXml} — o schema da NF-e rejeita.`,
        encontrado: `${icms.grupo} · ${nomeXml} ${v}`,
        esperado: aceitos ? [...aceitos].join(', ') : null,
      });
    }
  }

  if (cfop) {
    const cfopSt = cfop.atributos.includes('SUBSTITUICAO_TRIBUTARIA');
    const cobradoAntes = cst.tratamento === 'COBRADO_ANTERIORMENTE';
    if (cfopSt && !cst.comSt && !cobradoAntes) {
      av.add({
        regra: 'IT-ICMS-04',
        resultado: 'DEPENDE',
        mensagem: `CFOP ${cfop.codigo} é de operação com ST, mas o ${rotulo} ${icms.cst} não tem ST nem ICMS cobrado anteriormente.`,
        encontrado: `CFOP ${cfop.codigo} · ${rotulo} ${icms.cst}`,
        esperado: 'CST com ST (10, 30, 70, 201–203) ou cobrado anteriormente (60, 500)',
      });
    } else if (!cfopSt && (cst.comSt || cobradoAntes)) {
      av.add({
        regra: 'IT-ICMS-04',
        resultado: 'DEPENDE',
        mensagem: `${rotulo} ${icms.cst} (${cst.comSt ? 'retenção de ST' : 'ICMS cobrado anteriormente por ST'}) com CFOP ${cfop.codigo}, que não é de operação com ST.`,
        encontrado: `CFOP ${cfop.codigo} · ${rotulo} ${icms.cst}`,
        esperado: 'CFOP do subgrupo de ST (x.401–x.415) ou CST sem ST',
      });
    }
  }

  if (cst.tratamento === 'COBRADO_ANTERIORMENTE' && !icms.temStRetido) {
    av.add({
      regra: 'IT-ICMS-05',
      resultado: 'DEPENDE',
      mensagem: `${rotulo} ${icms.cst} sem os valores da ST retida anteriormente (vBCSTRet / vICMSSTRet).`,
      encontrado: `${icms.grupo}/${icms.cst}`,
    });
  }
}

function regraSentidoCst(
  nota: NotaLida,
  tabela: 'IPI' | 'PIS' | 'COFINS',
  cst: string | null,
  regra: string,
  base: BaseConhecimento,
  av: Avaliacao,
) {
  if (!cst) return;
  const info = base.cst.get(`${tabela}|${cst}`);
  if (!info) {
    av.add({ regra, resultado: 'DIVERGENTE', mensagem: `CST de ${tabela} ${cst} inexistente.`, encontrado: cst });
    return;
  }
  const sentidoNota = nota.tpNF === '1' ? 'saida' : nota.tpNF === '0' ? 'entrada' : null;
  if (sentidoNota && info.sentido !== 'ambos' && info.sentido !== sentidoNota) {
    av.add({
      regra,
      resultado: 'DIVERGENTE',
      mensagem: `CST de ${tabela} ${cst} é de ${SENTIDO_TEXTO[info.sentido] ?? info.sentido} e a nota é de ${SENTIDO_TEXTO[sentidoNota]}.`,
      encontrado: `${tabela} ${cst} · tpNF ${nota.tpNF}`,
      esperado: `CST de ${tabela} de ${SENTIDO_TEXTO[sentidoNota]}`,
    });
  }
}

function regraNcm(nota: NotaLida, item: ItemLido, base: BaseConhecimento, av: Avaliacao) {
  const ncm = item.ncm;
  if (!ncm) {
    av.add({ regra: 'IT-NCM-01', resultado: 'DIVERGENTE', mensagem: 'Item sem NCM.' });
    return;
  }
  if (/^0+$/.test(ncm)) {
    av.add({ regra: 'IT-NCM-01', resultado: 'DEPENDE', severidade: 'info', mensagem: `NCM genérico ${ncm} (usado para serviço ou item sem classificação).`, encontrado: ncm });
    return;
  }
  const info = base.ncm.get(ncm);
  if (!info) {
    av.add({ regra: 'IT-NCM-01', resultado: 'DIVERGENTE', mensagem: `NCM ${ncm} não existe na nomenclatura do Siscomex.`, encontrado: ncm });
  } else if (info.digitos !== 8) {
    av.add({ regra: 'IT-NCM-01', resultado: 'DIVERGENTE', mensagem: `NCM ${ncm} é agrupador (${info.digitos} dígitos), não código válido para NF-e.`, encontrado: ncm });
  } else if (foraDaVigencia(nota.dataEmissao, info.vigenciaInicio, info.vigenciaFim)) {
    av.add({
      regra: 'IT-NCM-01',
      resultado: 'DIVERGENTE',
      mensagem: `NCM ${ncm} fora de vigência em ${nota.dataEmissao}.`,
      encontrado: nota.dataEmissao,
      esperado: `${info.vigenciaInicio ?? '—'} a ${info.vigenciaFim ?? 'sem término'}`,
    });
  }
}

function regraIbsCbs(nota: NotaLida, item: ItemLido, base: BaseConhecimento, av: Avaliacao) {
  const g = item.ibscbs;
  if (!g) return;
  if (!g.cClassTrib) {
    av.add({ regra: 'IT-IBS-01', resultado: 'DIVERGENTE', mensagem: 'Grupo IBSCBS sem cClassTrib.', encontrado: `CST ${g.cst ?? '—'}` });
    return;
  }
  const info = base.cclassTrib.get(g.cClassTrib);
  if (!info) {
    av.add({ regra: 'IT-IBS-01', resultado: 'DIVERGENTE', mensagem: `cClassTrib ${g.cClassTrib} não existe na tabela oficial.`, encontrado: g.cClassTrib });
    return;
  }
  if (g.cst && info.cst !== g.cst) {
    av.add({
      regra: 'IT-IBS-01',
      resultado: 'DIVERGENTE',
      mensagem: `cClassTrib ${g.cClassTrib} pertence ao CST ${info.cst}, não ao CST ${g.cst} informado.`,
      encontrado: `CST ${g.cst} · cClassTrib ${g.cClassTrib}`,
      esperado: `CST ${info.cst}`,
    });
  }
  if (foraDaVigencia(nota.dataEmissao, info.vigenciaInicio, info.vigenciaFim)) {
    av.add({ regra: 'IT-IBS-01', resultado: 'DIVERGENTE', mensagem: `cClassTrib ${g.cClassTrib} fora de vigência em ${nota.dataEmissao}.`, encontrado: nota.dataEmissao });
  }
  const doc = nota.modelo ? MODELOS_NFE[nota.modelo] : undefined;
  if (doc && !info.documentos.has(doc)) {
    av.add({
      regra: 'IT-IBS-01',
      resultado: 'DIVERGENTE',
      mensagem: `cClassTrib ${g.cClassTrib} não é permitido em ${NOME_DOCUMENTO[doc] ?? doc}.`,
      encontrado: `modelo ${nota.modelo}`,
      esperado: [...info.documentos].join(', ') || 'nenhum documento',
    });
  }
}

/**
 * DIFAL/FCP (EC 87/2015; LC 87/96 art. 4º §2º, redação da LC 190/2022; Convênio ICMS 236/21 cl. 1ª): o remetente só
 * responde pelo DIFAL — e o destaca no grupo ICMSUFDest — quando o destinatário NÃO é contribuinte do ICMS. Contribuinte,
 * com IE (indIEDest = 1) ou isento de inscrição (indIEDest = 2, é contribuinte dispensado do cadastro), recolhe o DIFAL
 * ele mesmo. IT-DIFAL-01 segue a regra de validação da SEFAZ (MOC, rejeição 694): grupo exigido só com idDest = 2,
 * indFinal = 1 e indIEDest = 9, e fora das exceções do próprio MOC — NF-e de entrada, emitente do Simples Nacional
 * (CRT 1), item isento/não tributado/imune (CST 40/41, CSOSN 103/300/400) e nota com ICMS-ST informado ou retido.
 * A alíquota interestadual (pICMSInter) é fixa por par de
 * UF: 7% (Sul/Sudeste exceto ES → demais UFs) ou 12% (demais casos) — Resolução do Senado 22/89 e 13/2012.
 * O caso de 4% (bem com conteúdo de importação > 40%) depende de atributo do produto que a base não tem,
 * então não é reprovado. `pFCPUFDest` (IT-DIFAL-03) é conferido pela regra de FCP da UF de destino para o NCM do item
 * (fcpEsperado, tabelas fiscal_fcp_*_regra validadas em 30/09/2026); UF sem regra cadastrada cai na alíquota fixa da
 * fiscal_fcp_uf (Portal NF-e), como antes.
 */
type FcpEsperado = { aliquota: number; motivo: string; tipo: 'lista' | 'fora_lista' | 'geral' | 'sem_fcp' | 'nao_preencher' };

/**
 * FCP esperado no destino para o NCM do item, pelas regras validadas (fiscal_fcp_uf_regra + fiscal_fcp_ncm_regra). A regra
 * alcança o item quando o NCM começa por algum prefixo dela e por nenhuma exclusão; entre várias, vale a mais específica
 * (prefixo mais longo). Nulo = não dá para concluir (UF sem regra cadastrada, indefinida, ou adicional geral sem alíquota).
 */
export function fcpEsperado(uf: string, ncm: string | null, base: BaseConhecimento): FcpEsperado | null {
  const r = base.fcpUfRegra.get(uf);
  if (!r || r.forma === 'indefinida') return null;
  if (r.forma === 'sem_fcp') return { aliquota: 0, motivo: `${uf} não tem FCP — o campo deveria ser 0 ou vazio.`, tipo: 'sem_fcp' };
  if (r.naoPreencher) return { aliquota: 0, motivo: `em ${uf} o FCP já está embutido na alíquota interna — a SEFAZ orienta não preencher o campo de FCP.`, tipo: 'nao_preencher' };
  const regras = base.fcpNcm.get(uf) ?? [];
  const d = (ncm ?? '').replace(/\D/g, '');
  let melhor: { regra: FcpNcmRegra; tam: number } | null = null;
  if (d) {
    for (const regra of regras) {
      if (regra.aliquota === null || regra.exclusoes.some((e) => d.startsWith(e))) continue;
      const tam = Math.max(0, ...regra.prefixos.filter((p) => d.startsWith(p)).map((p) => p.length));
      if (tam > 0 && (!melhor || tam > melhor.tam)) melhor = { regra, tam };
    }
  }
  if (melhor) {
    const { regra } = melhor;
    return { aliquota: regra.aliquota as number, motivo: `a regra de FCP de ${uf} para "${regra.produto}" (NCM ${regra.ncm}${regra.dispositivo ? `, ${regra.dispositivo}` : ''}) é ${regra.aliquota}%.`, tipo: 'lista' };
  }
  if (r.forma === 'geral' || r.forma === 'hibrida') {
    const geral = regras.find((x) => !x.prefixos.length && /^todas/i.test(x.ncm) && x.aliquota !== null);
    return geral ? { aliquota: geral.aliquota as number, motivo: `o adicional geral de ${uf} é ${geral.aliquota}% (${geral.dispositivo ?? 'regra da UF'}); confira se o produto está em alguma exceção.`, tipo: 'geral' } : null;
  }
  return { aliquota: 0, motivo: `o NCM não está na lista de produtos com FCP de ${uf} — esperado 0%. Confira se a lista cadastrada está completa.`, tipo: 'fora_lista' };
}

const SEM_DEBITO_DE_ICMS = new Set(['ICMS_CST|40', 'ICMS_CST|41', 'ICMS_CSOSN|103', 'ICMS_CSOSN|300', 'ICMS_CSOSN|400']);

/** Casos em que o MOC exige o grupo ICMSUFDest no item (rejeição 694) — já sabendo que é interestadual a consumidor final. */
function difalDoRemetente(nota: NotaLida, item: ItemLido): boolean {
  if (nota.dest.indIEDest !== '9') return false; // contribuinte (com IE ou isento de inscrição): o DIFAL é do destinatário
  if (nota.tpNF === '0' || nota.emit.crt === '1') return false;
  if (item.icms?.cst && SEM_DEBITO_DE_ICMS.has(`${item.icms.tabela}|${item.icms.cst}`)) return false;
  return !nota.itens.some((i) => (i.icms?.vICMSST ?? 0) > 0 || i.icms?.temStRetido);
}

function regraDifal(nota: NotaLida, item: ItemLido, base: BaseConhecimento, av: Avaliacao) {
  if (nota.idDest !== '2' || nota.indFinal !== '1') return;
  const g = item.icmsUFDest;
  if (!g) {
    if (!difalDoRemetente(nota, item)) return;
    av.add({
      regra: 'IT-DIFAL-01',
      resultado: 'DIVERGENTE',
      mensagem: 'Operação interestadual a consumidor final não contribuinte sem o grupo de partilha do ICMS (ICMSUFDest) — DIFAL/FCP não informado.',
      encontrado: `idDest 2 · indFinal 1 · indIEDest ${nota.dest.indIEDest ?? '—'} · ${nota.emit.uf ?? '—'}→${nota.dest.uf ?? '—'}`,
    });
    return;
  }
  if (g.pICMSInter === null || !nota.emit.uf || !nota.dest.uf) return; // campo ausente ou UF desconhecida: nada a comparar
  if (g.pICMSInter === 4) {
    av.add({
      regra: 'IT-DIFAL-02',
      resultado: 'DEPENDE',
      severidade: 'info',
      mensagem: 'pICMSInter de 4% só é correto se o bem tem conteúdo de importação superior a 40% (Resolução do Senado 13/2012) — a base não verifica esse atributo do produto.',
      encontrado: '4%',
    });
    return;
  }
  const oficial = base.aliquotaInterestadual.get(`${nota.emit.uf}${nota.dest.uf}`);
  if (oficial !== undefined && g.pICMSInter !== oficial) {
    av.add({
      regra: 'IT-DIFAL-02',
      resultado: 'DIVERGENTE',
      mensagem: `pICMSInter de ${g.pICMSInter}% não é a alíquota interestadual oficial para ${nota.emit.uf}→${nota.dest.uf}.`,
      encontrado: `${g.pICMSInter}%`,
      esperado: `${oficial}%`,
    });
  }

  if (!nota.dest.uf) return;
  const esperado = fcpEsperado(nota.dest.uf, item.ncm, base);
  if (esperado) {
    const informado = g.pFCPUFDest ?? 0;
    if (Math.abs(informado - esperado.aliquota) <= 0.005) return;
    // Erro só quando a regra é taxativa (produto da lista, UF sem FCP); fora da lista, adicional geral e PE ficam como alerta —
    // a lista cadastrada pode estar incompleta e as exceções do adicional geral não estão todas no cadastro.
    const taxativa = esperado.tipo === 'lista' || esperado.tipo === 'sem_fcp';
    av.add({
      regra: 'IT-DIFAL-03',
      resultado: taxativa ? 'DIVERGENTE' : 'DEPENDE',
      ...(taxativa ? {} : { severidade: 'alerta' as const }),
      mensagem: `pFCPUFDest de ${informado}% em ${nota.dest.uf}, NCM ${item.ncm ?? '—'}: ${esperado.motivo}`,
      encontrado: g.pFCPUFDest === null ? 'não informado' : `${informado}%`,
      esperado: `${esperado.aliquota}%`,
    });
    return;
  }
  if (g.pFCPUFDest === null) return;
  const fcpOficial = base.fcpUf.get(nota.dest.uf);
  if (fcpOficial !== undefined && g.pFCPUFDest !== fcpOficial) {
    av.add({
      regra: 'IT-DIFAL-03',
      resultado: 'DIVERGENTE',
      mensagem: `pFCPUFDest de ${g.pFCPUFDest}% não é a alíquota de FCP oficial de ${nota.dest.uf} (${fcpOficial}%).`,
      encontrado: `${g.pFCPUFDest}%`,
      esperado: `${fcpOficial}%`,
    });
  }
}

/**
 * IPI na base do ICMS (LC 87/96, art. 13 §2º): o IPI só fica FORA da base do ICMS na operação entre contribuintes com
 * produto para industrialização/comercialização pelo destinatário. O critério é o indFinal da nota (ipiBaseIcms.ts):
 * indFinal 0 (normal) → sem IPI na base; indFinal 1 (consumidor final) → com IPI na base. Sem indFinal, decide pela
 * operação: destinatário não contribuinte, ou CFOP de uso e consumo/ativo imobilizado/não contribuinte, leva o IPI.
 *
 * Devolução (finNFe 4) fica de fora: repete a base da nota de origem.
 *
 * indFinal 0 com destinatário não contribuinte ou CFOP de uso e consumo/ativo é a própria nota em contradição: a base
 * pode seguir o indFinal e mesmo assim estar errada (é venda para consumo). Aí a regra avisa para conferir o indFinal.
 *
 * Alerta, não erro: só avalia no caso mais seguro — CST cujo tratamento é `TRIBUTADO` (modBC = 3,
 * "valor da operação", sem redução de base, sem ST cobrada anteriormente, sem diferimento/isenção;
 * exclui Simples Nacional). Base reduzida (`TRIBUTADO_REDUCAO_BC`, ex.: CST 70) muda a fórmula e fica
 * de fora — não é o mesmo cálculo. A base esperada usa vDesc/vFrete/vSeg/vOutro/vII do próprio item —
 * ausência desses campos aditivos é tratada como zero (ao contrário dos campos de tributo). Ainda
 * assim, casos de rateio de despesas da nota inteira entre itens ou benefícios não mapeados podem
 * gerar divergência aparente; por isso fica como alerta, não erro.
 */
function regraIpiNaBaseIcms(nota: NotaLida, item: ItemLido, cfop: CfopBase | null, base: BaseConhecimento, av: Avaliacao) {
  // Devolução (finNFe 4) repete a base do ICMS da nota de origem: o indFinal/destinatário da devolução não decide o IPI.
  if (nota.finNFe === '4') return;
  const icms = item.icms;
  if (!icms || icms.tabela !== 'ICMS_CST' || icms.modBC !== '3' || icms.vBC === null) return;
  const cst = icms.cst ? base.cst.get(`${icms.tabela}|${icms.cst}`) : undefined;
  if (!cst || cst.tratamento !== 'TRIBUTADO') return;
  const bases = basesIcmsComESemIpi(item);
  if (!bases || !cfop) return; // sem IPI no item, nada a compor

  const destNaoContribuinte = nota.dest.indIEDest !== '1';
  const cfopIndicaUsoOuNaoContribuinte = cfop.atributos.includes('USO_CONSUMO') || cfop.atributos.includes('ATIVO_IMOBILIZADO') || cfop.atributos.includes('NAO_CONTRIBUINTE');
  const pelaOperacao = destNaoContribuinte || cfopIndicaUsoOuNaoContribuinte;
  const motivoOperacao = destNaoContribuinte ? 'destinatário não contribuinte' : 'CFOP de uso/consumo, ativo imobilizado ou não contribuinte';
  const pelaNota = ipiPeloIndFinal(nota.indFinal);
  const deveIncluirIpi = pelaNota ? pelaNota === 'INCLUI' : pelaOperacao;
  // indFinal 0 numa operação que é de consumo: a nota se contradiz.
  const contradicao = pelaNota === 'EXCLUI' && pelaOperacao;

  const baseEsperada = deveIncluirIpi ? bases.comIpi : bases.semIpi;
  const ipiTxt = bases.vIPI.toFixed(2);
  if (Math.abs(baseEsperada - icms.vBC) > TOLERANCIA_BASE_IPI) {
    const conta = `base esperada ${baseEsperada.toFixed(2)} (${deveIncluirIpi ? 'com' : 'sem'} o IPI de ${ipiTxt}), informada ${icms.vBC.toFixed(2)}.`;
    av.add({
      regra: 'IT-ICMS-06',
      resultado: 'DIVERGENTE',
      mensagem:
        pelaNota === 'INCLUI'
          ? `indFinal 1 (consumidor final): o IPI compõe a base do ICMS — ${conta}`
          : pelaNota === 'EXCLUI'
            ? `indFinal 0 (operação normal): o IPI não compõe a base do ICMS — ${conta}${contradicao ? ` Mas a operação tem ${motivoOperacao}: se a mercadoria é para uso e consumo, a nota deveria ter indFinal 1 e o IPI na base.` : ''}`
            : deveIncluirIpi
              ? `IPI deveria compor a base do ICMS (${motivoOperacao}): ${conta}`
              : `IPI não deveria compor a base do ICMS (operação entre contribuintes, CFOP de revenda/industrialização): ${conta}`,
      encontrado: icms.vBC.toFixed(2),
      esperado: baseEsperada.toFixed(2),
    });
    return;
  }
  if (contradicao) {
    av.add({
      regra: 'IT-ICMS-06',
      resultado: 'DEPENDE',
      mensagem: `A base segue o indFinal 0 (sem o IPI de ${ipiTxt}), mas a operação tem ${motivoOperacao}: venda para uso e consumo é operação com consumidor final. Confira o indFinal — com indFinal 1 o IPI compõe a base do ICMS (base ${bases.comIpi.toFixed(2)}).`,
      encontrado: `indFinal 0 · base ${icms.vBC.toFixed(2)}`,
      esperado: `indFinal 1 · base ${bases.comIpi.toFixed(2)}`,
    });
  }
}

/**
 * MVA-ST em operação interna (LC 87/96 + Convênio ICMS 52/17): confere `pMVAST` contra a MVA oficial da
 * UF para o CEST do item — fonte: Portal Nacional da Substituição Tributária (CONFAZ), carregado UF por
 * UF (12 UFs pelo Portal Nacional da ST e MT pela Portaria 195/2019 — ver BC-D4). Alerta, não erro: só compara no caso mais restrito —
 * operação interna (emitente e destinatário na mesma UF já carregada), `modBCST = 4` (a UF declarou
 * que usa MVA, não pauta/preço tabelado) e a UF confirma ST ativa para esse CEST em operação interna
 * (`op_interna` "S" ou "SIM" — as planilhas usam as duas grafias, às vezes na mesma UF). Não cobre operação interestadual (MVA ajustada por alíquota, fora de escopo
 * ainda) nem UF que usa PFC/pauta em vez de MVA para o CEST.
 */
/**
 * Art. 2°-A da Portaria SEFAZ-MT 195/2019: atacadista que cumpre 4 condições aplica 50% da MVA por item.
 * Só MT tem isso, e a NF-e não diz se o emitente se enquadra — então o valor reduzido é só mais um valor
 * aceito, nunca exigido. Aceitar "metade da MVA" em qualquer outra UF silenciaria erro de verdade.
 */
const REDUCAO_MVA_ATACADISTA: Record<string, number> = { MT: 0.5 };

function regraIcmsSt(nota: NotaLida, item: ItemLido, base: BaseConhecimento, av: Avaliacao) {
  const icms = item.icms;
  if (!icms || icms.modBCST !== '4' || icms.pMVAST === null) return;
  if (!item.cest || !nota.emit.uf || !nota.dest.uf || nota.emit.uf !== nota.dest.uf) return;
  const pMVAST = icms.pMVAST;
  const uf = nota.dest.uf;
  const cest = item.cest.replace(/\D/g, '');
  const aceitos: { rotulo: string; mva: number }[] = [];

  const porCest = base.icmsStUf.get(`${uf}|${cest}`);
  // "S" (SC, GO, PA, parte da BA) e "SIM" (parte da BA) são a mesma coisa — a planilha não é consistente nem dentro da mesma UF.
  if (porCest && (porCest.opInterna === 'S' || porCest.opInterna === 'SIM') && porCest.mva !== null) {
    aceitos.push({ rotulo: 'MVA por CEST', mva: porCest.mva });
  }
  // UF cuja fonte tem mais de um regime ou mais de uma MVA por CEST (MT): qualquer valor aceito silencia a regra.
  const reducao = REDUCAO_MVA_ATACADISTA[uf];
  for (const f of base.icmsStUfFaixa.get(uf) ?? []) {
    if (cest.length !== 7 || cest < f.cestInicio || cest > f.cestFim) continue;
    // "(optantes)"/"(não optantes)" é vocabulário do Art. 1° §1° da Portaria SEFAZ-MT 195/2019 — só MT
    // tem essa distinção (regime 'faixa' só existe pra MT). RS e PB também usam regime 'item' (mais de
    // um valor aceito por CEST), mas por outro motivo (frete incluído/não incluído; regime de
    // fidelidade do fabricante) — rótulo neutro nessas UFs pra não citar um dispositivo que não existe lá.
    aceitos.push({ rotulo: f.regime === 'item' ? (uf === 'MT' ? 'MVA por item (optantes)' : 'MVA por item') : `faixa ${f.cestInicio}–${f.cestFim} (não optantes)`, mva: f.mva });
    if (reducao && f.regime === 'item') {
      aceitos.push({ rotulo: 'MVA por item com redução de 50% (atacadista, Art. 2°-A)', mva: Math.round(f.mva * reducao * 100) / 100 });
    }
  }
  if (!aceitos.length) return;
  if (aceitos.some((a) => Math.abs(pMVAST - a.mva) <= 0.01)) return;

  av.add({
    regra: 'IT-ST-01',
    resultado: 'DIVERGENTE',
    mensagem: aceitos.length === 1
      ? `MVA-ST de ${pMVAST}% para o CEST ${item.cest} não é a MVA oficial de ${uf} em operação interna (${aceitos[0].mva}%).`
      : `MVA-ST de ${pMVAST}% para o CEST ${item.cest} não bate com nenhuma MVA oficial de ${uf} em operação interna: ${aceitos.map((a) => `${a.mva}% (${a.rotulo})`).join(', ')}.`,
    encontrado: `${pMVAST}%`,
    esperado: aceitos.map((a) => `${a.mva}%`).join(' | '),
  });
}

/**
 * IT-ST-02 — recálculo da base e do valor do ICMS-ST com os parâmetros que o próprio XML declara (MVA, redução, alíquota-ST).
 * Só para modBCST = 4 (MVA) — pauta e preço máximo dependem de valor externo. Não julga a MVA (IT-ST-01) nem a alíquota:
 * confere se a conta fecha. Tolerância de R$ 0,05 ou 0,1% do valor esperado (o que for maior), porque emissores arredondam
 * a base por item ou por operação. FCP-ST fica de fora (vem em campos próprios).
 * CST 70 sem pRedBCST declarado fica de fora da conferência da base: na prática os emissores reduzem a base-ST e não informam
 * o percentual (calibrado em 582 itens reais de ST — CST 10 fecha 100% com o IPI na base; CST 70 vem ~7,7% abaixo).
 */
function regraIcmsStRecalculo(item: ItemLido, av: Avaliacao) {
  const icms = item.icms;
  if (!icms || icms.modBCST !== '4' || icms.vBCST === null || icms.vBCST <= 0) return;
  const tol = (esperado: number) => Math.max(0.05, Math.abs(esperado) * 0.001);
  const brl = (n: number) => n.toFixed(2);

  const reducaoNaoDeclarada = icms.cst === '70' && icms.tabela === 'ICMS_CST' && icms.pRedBCST === null;
  if (icms.pMVAST !== null && !reducaoNaoDeclarada) {
    const bruto = item.vProd + (item.vFrete ?? 0) + (item.vSeg ?? 0) + (item.vOutro ?? 0) - (item.vDesc ?? 0) + (item.vII ?? 0) + (item.ipi?.vTributo ?? 0);
    const esperada = bruto * (1 + icms.pMVAST / 100) * (1 - (icms.pRedBCST ?? 0) / 100);
    if (Math.abs(icms.vBCST - esperada) > tol(esperada)) {
      av.add({
        regra: 'IT-ST-02',
        resultado: 'DEPENDE',
        mensagem: `Base do ICMS-ST (${brl(icms.vBCST)}) não bate com o recálculo pela MVA declarada: (produto + frete + seguro + outras − desconto + II + IPI) × (1 + ${icms.pMVAST}%)${icms.pRedBCST ? ` × (1 − ${icms.pRedBCST}%)` : ''} = ${brl(esperada)}. Pode ser MVA ajustada, base de cálculo própria da UF ou frete/desconto rateado de outro modo.`,
        encontrado: brl(icms.vBCST),
        esperado: brl(esperada),
      });
    }
  }

  // ICMS-ST = base-ST × alíquota-ST − ICMS próprio. No Simples (CSOSN) o ICMS próprio não vem no grupo, então fica de fora.
  if (icms.pICMSST !== null && icms.vICMSST !== null && icms.tabela === 'ICMS_CST') {
    const esperado = (icms.vBCST * icms.pICMSST) / 100 - (icms.vICMS ?? 0);
    if (Math.abs(icms.vICMSST - esperado) > tol(esperado)) {
      av.add({
        regra: 'IT-ST-02',
        resultado: 'DIVERGENTE',
        mensagem: `Valor do ICMS-ST (${brl(icms.vICMSST)}) não fecha: base-ST ${brl(icms.vBCST)} × ${icms.pICMSST}% − ICMS próprio ${brl(icms.vICMS ?? 0)} = ${brl(esperado)}.`,
        encontrado: brl(icms.vICMSST),
        esperado: brl(esperado),
      });
    }
  }
}

/**
 * IT-CBENEF-01 — cBenef do item × tabela oficial "cBenef x CST" da UF do emitente. Só onde a base tem dado (PR, RJ, RS, publicado
 * até 31/01/2020): nas demais UFs não há o que comparar e a regra fica em silêncio. Código fora da tabela é DEPENDE (a tabela
 * é antiga); CST fora da lista de CSTs permitidos do código é DIVERGENTE.
 */
function regraCbenef(nota: NotaLida, item: ItemLido, base: BaseConhecimento, av: Avaliacao) {
  const uf = nota.emit.uf;
  const codigo = item.cBenef?.trim().toUpperCase();
  if (!uf || !codigo || !base.ufsComCbenef.has(uf)) return;
  const permitidos = base.cbenef.get(`${uf}|${codigo}`);
  if (permitidos === undefined) {
    av.add({
      regra: 'IT-CBENEF-01',
      resultado: 'DEPENDE',
      mensagem: `cBenef ${codigo} não consta da tabela oficial de ${uf} (NT 2019.001, publicada até 31/01/2020) — pode ser código criado depois; confira na SEFAZ.`,
      encontrado: codigo,
    });
    return;
  }
  const cst = item.icms?.cst;
  if (!cst || item.icms?.tabela !== 'ICMS_CST' || !permitidos.length || permitidos.includes(cst)) return;
  av.add({
    regra: 'IT-CBENEF-01',
    resultado: 'DIVERGENTE',
    mensagem: `cBenef ${codigo} de ${uf} só é permitido com CST ${permitidos.join(', ')}; o item usa CST ${cst}.`,
    encontrado: `CST ${cst}`,
    esperado: `CST ${permitidos.join(' | ')}`,
  });
}

export function avaliarItem(nota: NotaLida, item: ItemLido, base: BaseConhecimento, av: Avaliacao): CfopBase | null {
  if (ehNfse(nota)) {
    // NFS-e: sem CFOP, NCM, ICMS e IPI. Vale a conta base × alíquota (ISS, PIS, COFINS), o cClassTrib e as regras SV-*.
    conferirValor('ISSQN', item.issqn?.vBC ?? null, item.issqn?.vAliq ?? null, item.issqn?.vISSQN ?? null, av);
    conferirValor('PIS', item.pis?.vBC ?? null, item.pis?.pAliquota ?? null, item.pis?.vTributo ?? null, av);
    conferirValor('COFINS', item.cofins?.vBC ?? null, item.cofins?.pAliquota ?? null, item.cofins?.vTributo ?? null, av);
    regraIbsCbs(nota, item, base, av);
    avaliarServico(nota, item, base, av);
    return null;
  }
  const cfop = regrasCfop(nota, item, base, av);
  regrasIcms(nota, item, cfop, base, av);
  regraDifal(nota, item, base, av);
  regraIpiNaBaseIcms(nota, item, cfop, base, av);
  regraIcmsSt(nota, item, base, av);
  regraIcmsStRecalculo(item, av);
  regraCbenef(nota, item, base, av);
  conferirIcms(item.icms, av);
  conferirValor('ISSQN', item.issqn?.vBC ?? null, item.issqn?.vAliq ?? null, item.issqn?.vISSQN ?? null, av);
  conferirValor('IPI', item.ipi?.vBC ?? null, item.ipi?.pAliquota ?? null, item.ipi?.vTributo ?? null, av);
  conferirValor('PIS', item.pis?.vBC ?? null, item.pis?.pAliquota ?? null, item.pis?.vTributo ?? null, av);
  conferirValor('COFINS', item.cofins?.vBC ?? null, item.cofins?.pAliquota ?? null, item.cofins?.vTributo ?? null, av);
  regraSentidoCst(nota, 'IPI', item.ipi?.cst ?? null, 'IT-IPI-01', base, av);
  regraSentidoCst(nota, 'PIS', item.pis?.cst ?? null, 'IT-PISCOF-01', base, av);
  regraSentidoCst(nota, 'COFINS', item.cofins?.cst ?? null, 'IT-PISCOF-01', base, av);
  if (item.pis?.cst && item.cofins?.cst && item.pis.cst !== item.cofins.cst) {
    av.add({
      regra: 'IT-PISCOF-02',
      resultado: 'DEPENDE',
      mensagem: `CST de PIS (${item.pis.cst}) diferente do CST de COFINS (${item.cofins.cst}).`,
      encontrado: `PIS ${item.pis.cst} · COFINS ${item.cofins.cst}`,
    });
  }
  regraNcm(nota, item, base, av);
  regraIbsCbs(nota, item, base, av);
  return cfop;
}
